import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'product-personalizer/src/store/hooks';

import getCustomFontTextTargets, {
  CustomFontTextTarget,
} from 'product-personalizer/src/utils/getCustomFontTextTargets';

import useCustomFontRender, { FontRenderResult } from './SpreadRenderer/useCustomFontRender';

// Latest API-rendered path per custom-font text element, keyed by element.uuid.
// TextElement reads from this to keep its desktop overlay smooth during the
// debounce window — element.path in Redux is cleared on every text edit,
// so without this buffer the overlay would flash off and back on per keystroke.
const CustomFontPathContext = React.createContext<ReadonlyMap<number, FontRenderResult>>(new Map());

export function useCustomFontPath(uuid: number): FontRenderResult | undefined {
  return useContext(CustomFontPathContext).get(uuid);
}

interface ManagerProps {
  target: CustomFontTextTarget;
  publish: (uuid: number, result: FontRenderResult | undefined) => void;
}

// One per custom-font text element. Owns its debounced fetch + Redux dispatch
// (path goes to element.path so the offscreen rasterizer in createSpreadPreview
// picks it up — that's what unblocks mobile previews) and publishes the same
// result into context for TextElement to consume.
function CustomFontRenderManager({ target, publish }: ManagerProps) {
  const result = useCustomFontRender(target.element, target.address);
  const { uuid } = target.element;

  useEffect(() => {
    publish(uuid, result);
  }, [uuid, result, publish]);

  useEffect(
    () => () => {
      publish(uuid, undefined);
    },
    [uuid, publish],
  );

  return null;
}

interface Props {
  children: React.ReactNode;
}

function CustomFontRenderOrchestrator({ children }: Props) {
  const designData = useSelector((state) => state.design.designData);
  const targets = useMemo(() => getCustomFontTextTargets(designData), [designData]);

  const [pathMap, setPathMap] = useState<Map<number, FontRenderResult>>(() => new Map());

  const publish = useCallback((uuid: number, result: FontRenderResult | undefined) => {
    setPathMap((prev) => {
      if (!result) {
        if (!prev.has(uuid)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(uuid);
        return next;
      }
      if (prev.get(uuid) === result) {
        return prev;
      }
      const next = new Map(prev);
      next.set(uuid, result);
      return next;
    });
  }, []);

  return (
    <CustomFontPathContext.Provider value={pathMap}>
      {targets.map((target) => (
        <CustomFontRenderManager key={target.element.uuid} target={target} publish={publish} />
      ))}
      {children}
    </CustomFontPathContext.Provider>
  );
}

export default CustomFontRenderOrchestrator;
